import { createAsyncThunk, createEntityAdapter, createSlice, createSelector } from '@reduxjs/toolkit';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import { apiGet, apiPut, apiDel } from 'app/shared-components/util/restAPI';

const clientServiceAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || '')
});

export const fetchClientServices = createAsyncThunk(
  'clientServices/fetchClientServices',
  async (ownerKey, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientservice/byowner/${ownerKey}`;
      const options = { headers: {}, response: false };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const createClientService = createAsyncThunk(
  'clientServices/createClientService',
  async ({ ownerKey, channelId, serviceData }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = '/x3/clientservice/';
      const body = { ...serviceData };
      if (channelId) {
        body.channelId = channelId;
      }
      const options = { headers: {}, body, response: false };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateClientService = createAsyncThunk(
  'clientServices/updateClientService',
  async ({ serviceId, updates }, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientservice/${serviceId}`;
      const body = { id: serviceId, ...updates };
      const options = { headers: {}, body, response: false };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const deleteClientService = createAsyncThunk(
  'clientServices/deleteClientService',
  async (serviceId, { dispatch }) => {
    dispatch(startLoading());
    try {
      const apiName = 'CoreAPI3';
      const path = `/x3/clientservice/${serviceId}`;
      const options = { headers: {}, response: false };
      await apiDel(apiName, path, options);
      return serviceId;
    } finally {
      dispatch(stopLoading());
    }
  }
);

const clientServicesSlice = createSlice({
  name: 'clientServices',
  initialState: clientServiceAdapter.getInitialState({
    loading: false,
    byClient: {}
  }),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchClientServices.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg;
        state.byClient[ownerKey] = action.payload.map(s => s.id);
        clientServiceAdapter.upsertMany(state, action.payload);
      })
      .addCase(createClientService.fulfilled, (state, action) => {
        const ownerKey = action.meta.arg.ownerKey;
        clientServiceAdapter.addOne(state, action.payload);
        if (!state.byClient[ownerKey]) state.byClient[ownerKey] = [];
        if (!state.byClient[ownerKey].includes(action.payload.id)) {
          state.byClient[ownerKey].push(action.payload.id);
        }
      })
      .addCase(updateClientService.fulfilled, (state, action) => {
        clientServiceAdapter.upsertOne(state, action.payload);
      })
      .addCase(deleteClientService.fulfilled, (state, action) => {
        const serviceId = action.payload;
        clientServiceAdapter.removeOne(state, serviceId);
        Object.keys(state.byClient).forEach(clientId => {
          state.byClient[clientId] = state.byClient[clientId].filter(id => id !== serviceId);
        });
      });
  }
});

const { selectById, selectAll } = clientServiceAdapter.getSelectors();

const EMPTY_ARRAY = [];

export const selectServicesByOwnerKey = (ownerKey) => createSelector(
  [state => state.clientServices.byClient[ownerKey] || EMPTY_ARRAY, state => state.clientServices],
  (serviceIds, clientServicesState) => 
    serviceIds.map(id => selectById(clientServicesState, id)).filter(Boolean)
);

export const selectServiceById = (id) => state => selectById(state.clientServices, id);
export const selectServicesLoading = state => state.clientServices.loading;

export default clientServicesSlice.reducer;
